import type { StudyTerm } from './studyApi';

/** The end-of-Term wrap-up card appears this many days before the current Term ends (PRD §4.2). */
export const TERM_WRAP_UP_WINDOW_DAYS = 14;
/** "Not now" hides the wrap-up card for this many days. */
export const TERM_WRAP_UP_SNOOZE_DAYS = 3;
export const DEFAULT_STUDY_TIMEZONE = 'Australia/Brisbane';

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` for `now` in the academic year's timezone, so it compares with date-only strings. */
export function todayInTimezone(timezone: string | null | undefined, now: Date = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || DEFAULT_STUDY_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: DEFAULT_STUDY_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  }
}

/** Whole days from `from` to `to` (both `YYYY-MM-DD`). Negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

/** Three-letter month names. `Intl` gives "Sept" or "Sep" depending on the browser, so fix them. */
export const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** "13 Oct" for a date-only string, with no timezone shift. */
export function formatShortDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  return `${date.getUTCDate()} ${SHORT_MONTHS[date.getUTCMonth()]}`;
}

/** Number of school weeks in a Term with known dates (a partial last week counts). */
export function termWeekCount(startsOn: string, endsOn: string): number {
  return Math.max(1, Math.ceil((daysBetween(startsOn, endsOn) + 1) / 7));
}

export type TermPosition =
  | { kind: 'in_term'; week: number; weeksLeft: number }
  | { kind: 'holidays'; nextTermNumber: number; startsOn: string }
  | { kind: 'unknown' };

type PositionTerm = Pick<StudyTerm, 'term_number' | 'starts_on' | 'ends_on' | 'week'>;

/**
 * Where the learner is in the school year for the Home position line (PRD §4.1). Dates that are
 * not known never produce a week: the line then shows only the Term.
 */
export function termPosition(
  current: PositionTerm,
  terms: PositionTerm[],
  today: string,
): TermPosition {
  const { starts_on: startsOn, ends_on: endsOn } = current;
  if (!startsOn || !endsOn) return { kind: 'unknown' };
  // The server week wins when it has one: it is computed from the same dates on the server clock.
  if (current.week || (today >= startsOn && today <= endsOn)) {
    const week = current.week || Math.floor(daysBetween(startsOn, today) / 7) + 1;
    return { kind: 'in_term', week, weeksLeft: Math.max(0, termWeekCount(startsOn, endsOn) - week) };
  }
  if (today < startsOn)
    return { kind: 'holidays', nextTermNumber: current.term_number, startsOn };
  const next = terms.find((term) => term.term_number === current.term_number + 1);
  if (next?.starts_on && next.starts_on > today)
    return { kind: 'holidays', nextTermNumber: next.term_number, startsOn: next.starts_on };
  return { kind: 'unknown' };
}

export function weeksLeftLabel(weeksLeft: number): string {
  if (weeksLeft === 0) return 'Last week';
  return `${weeksLeft} ${weeksLeft === 1 ? 'week' : 'weeks'} left`;
}

export type WrapUpWindow =
  | { kind: 'before_window'; daysLeft: number; weeksLeft: number }
  | { kind: 'in_window'; daysLeft: number }
  | { kind: 'ended' }
  | { kind: 'dates_unknown' };

/** Where today sits against the end-of-Term wrap-up window (PRD §4.2, §5). */
export function wrapUpWindow(
  current: Pick<StudyTerm, 'starts_on' | 'ends_on'>,
  today: string,
  windowDays = TERM_WRAP_UP_WINDOW_DAYS,
): WrapUpWindow {
  if (!current.ends_on) return { kind: 'dates_unknown' };
  const daysLeft = daysBetween(today, current.ends_on);
  if (daysLeft < 0) return { kind: 'ended' };
  if (daysLeft <= windowDays) return { kind: 'in_window', daysLeft };
  return { kind: 'before_window', daysLeft, weeksLeft: Math.ceil(daysLeft / 7) };
}

export type WrapUpCardCopy = { message: string; action: string };

/** Home wrap-up card copy. Term 4 wraps up the whole school year. */
export function wrapUpCardCopy(
  termNumber: number,
  schoolYear: number,
  window: WrapUpWindow,
): WrapUpCardCopy | null {
  if (window.kind !== 'in_window' && window.kind !== 'ended') return null;
  if (termNumber >= 4)
    return {
      message: `${schoolYear} is nearly over. Wrap up the year and set up ${schoolYear + 1}.`,
      action: `Wrap up ${schoolYear}`,
    };
  const next = termNumber + 1;
  if (window.kind === 'ended')
    return {
      message: `Term ${termNumber} has ended. Wrap it up to start Term ${next}.`,
      action: `Wrap up Term ${termNumber}`,
    };
  const when =
    window.daysLeft === 0
      ? 'ends today'
      : `ends in ${window.daysLeft} ${window.daysLeft === 1 ? 'day' : 'days'}`;
  return {
    message: `Term ${termNumber} ${when}. When you're ready, wrap it up and set up Term ${next}.`,
    action: `Wrap up Term ${termNumber}`,
  };
}

/** Plain help description for a Study Session record (PRD §6.2). Never shows H0–H4. */
export function helpDescription(highestHintLevel: number | null | undefined): string {
  if (highestHintLevel === undefined || highestHintLevel === null) return 'No hints used';
  if (highestHintLevel <= 1) return 'Used a small hint';
  if (highestHintLevel <= 3) return 'Used step-by-step help';
  return 'Saw a full worked example';
}

export type WeekGroup<T> = { key: string; label: string; items: T[] };

/** Monday of the week that holds `day` (`YYYY-MM-DD`). */
function mondayOf(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  const offset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return date.toISOString().slice(0, 10);
}

/**
 * Groups records by school week, newest first. With known Term dates the label is the Term week
 * (`Week 5`); otherwise the calendar week (`Week of 6 Oct`).
 */
export function groupByWeek<T extends { at: string }>(
  items: T[],
  termStartsOn: string | null,
  timezone: string | null | undefined,
): WeekGroup<T>[] {
  const groups = new Map<string, WeekGroup<T>>();
  for (const item of items) {
    const day = todayInTimezone(timezone, new Date(item.at));
    const week =
      termStartsOn && day >= termStartsOn ? Math.floor(daysBetween(termStartsOn, day) / 7) + 1 : 0;
    const monday = mondayOf(day);
    const key = week > 0 ? `week-${week}` : `from-${monday}`;
    const label = week > 0 ? `Week ${week}` : `Week of ${formatShortDate(monday)}`;
    const group = groups.get(key) ?? { key, label, items: [] };
    group.items.push(item);
    groups.set(key, group);
  }
  const newest = (group: WeekGroup<T>) =>
    group.items.reduce((latest, item) => (item.at > latest ? item.at : latest), '');
  return [...groups.values()].sort((a, b) => newest(b).localeCompare(newest(a)));
}

export function activitiesLabel(count: number): string {
  return `${count} ${count === 1 ? 'activity' : 'activities'}`;
}
